// =========================================================================
// FINDING OWNERS — static, easily editable mapping table
// =========================================================================
// Maps each finding type (recommendation id or vendor category) to an owner
// role, with a concrete bilingual procedure ("what to hand to whom").
// Rules: own JS/DOM/CSS -> frontend; tags/containers -> GTM admin;
// ad slots/bidding -> ad ops; TTFB/cache/CDN -> infra; CMP/consent -> privacy+GTM.
// To change an assignment, edit REC_OWNER_MAP / CATEGORY_OWNER_MAP below.

export const OWNERS = {
  frontend: {
    label: { cs: 'Frontend', en: 'Frontend' },
    color: '#60a5fa',
    procedure: {
      cs: 'Zadat frontend vývojáři jako úpravu vlastního kódu (JS/DOM/CSS). Po nasazení ihned ověřit lab testem.',
      en: 'Assign to the frontend developer as a change to first-party code (JS/DOM/CSS). Verify with a lab test immediately after deploy.'
    }
  },
  gtm: {
    label: { cs: 'Správce GTM', en: 'GTM admin' },
    color: '#fbbf24',
    procedure: {
      cs: 'Zadat správci GTM — úprava tagů a kontejnerů (odstranění nevyužitých tagů, odklad spouštění, konsolidace). Po publikaci kontejneru ihned ověřit lab testem.',
      en: 'Assign to the GTM admin — tag/container change (remove unused tags, defer triggers, consolidate). Verify with a lab test right after publishing the container.'
    }
  },
  tagging: {
    label: { cs: 'Správce měření / integrací', en: 'Measurement / integrations owner' },
    color: '#fbbf24',
    procedure: {
      cs: 'Nejprve ověřit, jak se tagy nasazují a kdo je spravuje. Po změně porovnat měření; GTM kontejner nelze z požadavků gtag odvodit.',
      en: 'First verify how tags are deployed and who manages them. Compare measurements after changes; gtag requests do not establish a GTM container.'
    }
  },
  adops: {
    label: { cs: 'Ad ops', en: 'Ad ops' },
    color: '#f87171',
    procedure: {
      cs: 'Zadat ad ops týmu — úprava reklamních slotů / header biddingu (lazy-load pod ohybem, omezení partnerů). Po nasazení ihned ověřit lab testem.',
      en: 'Assign to the ad ops team — ad slot / header bidding change (lazy-load below the fold, limit partners). Verify with a lab test right after deploy.'
    }
  },
  infra: {
    label: { cs: 'Infrastruktura', en: 'Infrastructure' },
    color: '#34d399',
    procedure: {
      cs: 'Zadat správci infrastruktury/hostingu — TTFB, cache a CDN (nasazení CDN, komprese, cache pravidla). Po nasazení ihned ověřit lab testem.',
      en: 'Assign to the infrastructure/hosting admin — TTFB, cache and CDN (deploy CDN, compression, cache rules). Verify with a lab test right after deploy.'
    }
  },
  privacy: {
    label: { cs: 'Privacy + správce CMP', en: 'Privacy + CMP owner' },
    color: '#c084fc',
    procedure: {
      cs: 'Zadat společně privacy specialistovi a správci CMP — načítání lišty, pořadí spouštění a consent mode. Po změně ověřit lab testem v obou variantách souhlasu.',
      en: 'Assign jointly to the privacy specialist and CMP owner — banner loading, firing order and consent mode. Verify with a lab test in both consent states.'
    }
  }
};

// Recommendation id -> owner key (mirrors ids produced by
// server/services/performance/recommendationEngine.js coefficients).
export const REC_OWNER_MAP = {
  'gtm-server-side': 'gtm',
  'gtm-consolidate-containers': 'gtm',
  'remove-unused-gtm-tags': 'gtm',
  'defer-third-party-scripts': 'gtm',
  'lazy-load-iframes': 'frontend',
  'lazy-load-ads-below-fold': 'adops',
  'cdn-for-static-assets': 'infra',
  'image-cdn-auto-format': 'infra',
  'compress-text-resources': 'infra',
  'remove-render-blocking-css': 'frontend',
  'convert-to-modern-formats': 'frontend',
  'remove-unused-javascript': 'frontend'
};

// Vendor category -> owner key (fallback for vendor-based findings).
export const CATEGORY_OWNER_MAP = {
  'tag-manager': 'gtm',
  analytics: 'gtm',
  social: 'gtm',
  ads: 'adops',
  cmp: 'privacy',
  cdn: 'infra',
  chat: 'gtm',
  video: 'frontend',
  'first-party': 'frontend',
  'third-party': 'gtm'
};

// CLS shift cause -> owner key. Ad slots belong to ad ops (slot config /
// creatives), the consent banner to privacy+GTM, everything else (own DOM,
// images, embeds, fonts) to frontend.
export const CLS_CAUSE_OWNER_MAP = {
  'ad-injection': 'adops',
  'consent-banner': 'privacy',
  'unsized-image': 'frontend',
  'image-load': 'frontend',
  'iframe-embed': 'frontend',
  'video-embed': 'frontend',
  'font-swap': 'frontend',
  'dynamic-content': 'frontend',
  animation: 'frontend',
  unknown: 'frontend'
};

export const ownerForClsCause = (cause, lang = 'cs') => {
  const key = CLS_CAUSE_OWNER_MAP[cause] || 'frontend';
  const o = OWNERS[key];
  if (!o) return null;
  return { key, label: o.label[lang] || o.label.cs, color: o.color, procedure: o.procedure[lang] || o.procedure.cs };
};

// Returns { key, label, color, procedure } for a recommendation, or null.
export const ownerForRecommendation = (rec, lang = 'cs', { hasGtmContainer: containerEvidence } = {}) => {
  const assigned = rec?.owner || REC_OWNER_MAP[rec?.id] || null;
  const key = assigned === 'gtm' && containerEvidence === false ? 'tagging' : assigned;
  if (!key || !OWNERS[key]) return null;
  const o = OWNERS[key];
  return {
    key,
    label: o.label[lang] || o.label.cs,
    color: o.color,
    procedure: o.procedure[lang] || o.procedure.cs
  };
};

export const ownerForCategory = (category, lang = 'cs') => {
  const key = CATEGORY_OWNER_MAP[category] || null;
  if (!key || !OWNERS[key]) return null;
  const o = OWNERS[key];
  return { key, label: o.label[lang] || o.label.cs, color: o.color, procedure: o.procedure[lang] || o.procedure.cs };
};

// =========================================================================
// FOLLOW-UP MEASUREMENT SCHEDULE ("Kontrolní měření")
// =========================================================================
export const buildFollowUpSchedule = (auditDate = new Date(), deployedAt = null) => {
  const base = auditDate instanceof Date && !isNaN(auditDate) ? auditDate : new Date();
  const deployment = deployedAt == null ? null : new Date(deployedAt);
  const fieldDate = deployment && !isNaN(deployment) ? new Date(deployment.getTime()) : null;
  if (fieldDate) fieldDate.setDate(fieldDate.getDate() + 28);
  return { auditDate: base, fieldDate };
};

export const formatFollowUpDate = (date, lang) => {
  if (date == null) return lang === 'cs' ? '28 dní po nasazení (datum neznámé)' : '28 days after deployment (date unknown)';
  if (typeof date.getTime !== 'function' || !Number.isFinite(date.getTime())) return lang === 'cs' ? 'datum neznámé' : 'date unknown';
  return date.toLocaleDateString(lang === 'cs' ? 'cs-CZ' : 'en-US', {
    day: 'numeric', month: 'long', year: 'numeric'
  });
};

export const followUpLabels = (lang) => (lang === 'cs' ? {
  title: 'Kontrolní měření',
  labTitle: 'Lab re-test — ihned po nasazení',
  labText: 'Každou nasazenou změnu ověřte lab testem (Lighthouse / deep test) ihned po nasazení. Lab výsledek je k dispozici okamžitě a potvrdí, že změna má očekávaný technický efekt.',
  fieldTitle: 'Field (CrUX) kontrola',
  fieldTextBefore: 'Reálná data od uživatelů (CrUX) zkontrolujte nejdříve',
  fieldTextAfter: '— od nasazení změny, nikoli od data auditu.',
  windowExplain: 'CrUX počítá metriky z klouzavého 28denního okna reálných návštěv. Dokud okno neobsahuje pouze návštěvy po nasazení změny, field výsledek je směsí starého a nového stavu — hodnotit ho dřív by bylo zavádějící.',
  auditDateLabel: 'Datum auditu',
  fieldDateLabel: 'Field kontrola od'
} : {
  title: 'Follow-up measurements',
  labTitle: 'Lab re-test — immediately after deploy',
  labText: 'Verify every deployed change with a lab test (Lighthouse / deep test) immediately after deploy. The lab result is available instantly and confirms the change has the expected technical effect.',
  fieldTitle: 'Field (CrUX) check',
  fieldTextBefore: 'Check real-user data (CrUX) no earlier than',
  fieldTextAfter: '— measured from deployment, not the audit date.',
  windowExplain: 'CrUX computes metrics from a rolling 28-day window of real visits. Until the window contains only post-deploy visits, the field result mixes the old and new state — judging it earlier would be misleading.',
  auditDateLabel: 'Audit date',
  fieldDateLabel: 'Field check from'
});
